import type { SessionDto } from '@reelplicate/api-client/model';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const COUNTRIES = new Intl.DisplayNames('en', { type: 'region' });

/**
 * A country as a name: region codes ("GB") are spelled out, anything else
 * Clerk sends (it can be a name already) is shown as is.
 */
function countryName(country: string): string {
  if (!/^[A-Z]{2}$/.test(country)) return country;
  try {
    return COUNTRIES.of(country) ?? country;
  } catch {
    return country;
  }
}

/** Clerk's device names, as people know the OS. */
const OS: Record<string, string> = { Macintosh: 'macOS' };

/** "Chrome on macOS", "Safari on iPhone"; whichever half Clerk knows. */
export function sessionDevice(session: SessionDto): string {
  const os = session.deviceType && (OS[session.deviceType] ?? session.deviceType);
  if (session.browserName && os) return `${session.browserName} on ${os}`;
  return session.browserName ?? os ?? 'Unknown device';
}

/** "London, United Kingdom · 2 hours ago"; the current session is "active now". */
export function sessionMeta(session: SessionDto, now = new Date()): string {
  const country = session.country && countryName(session.country);
  const place = [session.city, country].filter(Boolean).join(', ');
  const ago = now.getTime() - new Date(session.lastActiveAt).getTime();
  const when = session.current
    ? 'active now'
    : ago < HOUR
      ? RELATIVE.format(-Math.max(1, Math.floor(ago / 60_000)), 'minute')
      : ago < DAY
        ? RELATIVE.format(-Math.floor(ago / HOUR), 'hour')
        : RELATIVE.format(-Math.floor(ago / DAY), 'day');
  return place ? `${place} · ${when}` : when;
}
